// Care plan: vaccinations, parasite control, vet checks, admin and grooming.
//
// A starter plan is generated from Arlo's date of birth and home date. Items
// are ordinary records afterwards, so the household can edit dates to match
// what their vet actually books. Generated items use deterministic ids so two
// devices generating the plan at the same time converge on one copy.

import { ageOn } from './age.ts';
import { addDays, addMonths, daysBetween, today } from './time.ts';
import type { CareItem, ISODate, Millis, Profile, Region, Repeat } from './types.ts';

export interface CarePlanInput {
  dob: ISODate;
  homeDate: ISODate;
  region: Region;
}

/** Days until the next occurrence of a repeating item, given the date it was done. */
export function nextDue(repeat: Repeat, doneOn: ISODate, dob?: ISODate): ISODate {
  if ('rule' in repeat) {
    const weeks = dob ? ageOn(dob, doneOn).weeks : 52;
    if (repeat.rule === 'puppyWorming') {
      if (weeks < 12) return addDays(doneOn, 14);
      return addMonths(doneOn, 1);
    }
    // puppyWeigh
    if (weeks < 26) return addDays(doneOn, 14);
    return addMonths(doneOn, 1);
  }
  if (repeat.unit === 'day') return addDays(doneOn, repeat.every);
  if (repeat.unit === 'week') return addDays(doneOn, repeat.every * 7);
  return addMonths(doneOn, repeat.every);
}

export function describeRepeat(repeat: Repeat): string {
  if ('rule' in repeat) {
    return repeat.rule === 'puppyWorming'
      ? 'Every 2 weeks to 12 weeks old, then monthly (follow your vet’s product)'
      : 'Every 2 weeks while growing, monthly from 6 months';
  }
  const unit = repeat.every === 1 ? repeat.unit : `${repeat.every} ${repeat.unit}s`;
  return `Every ${unit}`;
}

/** Generate the starter plan. */
export function generateCarePlan({ dob, homeDate, region }: CarePlanInput): CareItem[] {
  const w = (weeks: number) => addDays(dob, weeks * 7);
  const later = (a: ISODate, b: ISODate) => (a > b ? a : b);
  const items: CareItem[] = [];
  const add = (item: Omit<CareItem, 'id'> & { template: string }) => items.push({ id: `plan-${item.template}`, ...item });

  // --- Before he comes home -------------------------------------------------
  add({
    template: 'insurance',
    kind: 'admin',
    title: 'Arrange pet insurance',
    due: addDays(homeDate, -7),
    why: 'Have cover in place from the day he comes home. Lifetime policies cover ongoing conditions year after year; many breeders include a few weeks of free cover — check when it ends.',
    repeat: { every: 12, unit: 'month' },
  });
  add({
    template: 'register-vet',
    kind: 'admin',
    title: 'Register with a vet practice',
    due: addDays(homeDate, -7),
    why: 'Register before he arrives so a first health check and his second vaccination can be booked straight away.',
  });
  add({
    template: 'id-tag',
    kind: 'admin',
    title: 'Order an ID tag (your name & address)',
    due: addDays(homeDate, -5),
    why: 'By law (Control of Dogs Order 1992) a dog in a public place must wear a collar with the owner’s name and address on it or on a tag. A phone number is strongly advised.',
  });
  add({
    template: 'breeder-paperwork',
    kind: 'admin',
    title: 'Collect paperwork from the breeder',
    due: homeDate,
    why: 'Vaccination card, microchip number & database, worming/flea history, food he’s used to, parents’ health test results, Kennel Club papers if registered, and a blanket that smells of mum.',
  });

  // --- First weeks home -----------------------------------------------------
  add({
    template: 'microchip-transfer',
    kind: 'admin',
    title: 'Transfer the microchip into your names',
    due: addDays(homeDate, 3),
    why: 'Puppies must be microchipped by 8 weeks by the breeder, but it is the new keeper’s legal duty to make sure the database has your details. Out-of-date details are one of the main reasons lost dogs aren’t reunited.',
  });
  add({
    template: 'first-vet-check',
    kind: 'vet',
    title: 'First health check at the vet',
    due: addDays(homeDate, 3),
    why: 'A check-over in the first few days (often free with a new puppy) catches any problem early and makes the vet a nice place — take treats.',
  });

  // --- Vaccinations ---------------------------------------------------------
  add({
    template: 'vax-1',
    kind: 'vaccination',
    title: '1st vaccination',
    due: w(8),
    why: 'Covers distemper, hepatitis, parvovirus and leptospirosis. Often given by the breeder’s vet before collection — check his card.',
  });
  add({
    template: 'vax-2',
    kind: 'vaccination',
    title: '2nd vaccination',
    due: later(w(10), addDays(homeDate, 7)),
    why: 'Usually 2–4 weeks after the first. Protection builds over the following 1–3 weeks (the leptospirosis part takes longest) — ask your vet for his exact “go out” date and put it on the next item.',
  });
  add({
    template: 'out-and-about',
    kind: 'vet',
    title: 'Vet’s “go out” date — first proper walks!',
    due: addDays(later(w(10), addDays(homeDate, 7)), 14),
    why: 'Set this to the date your vet gives you (typically 1–3 weeks after the 2nd jab). Until then, carry him out to see the world — it’s prime socialisation time — and only mix with healthy, fully vaccinated dogs in private gardens.',
  });
  add({
    template: 'booster',
    kind: 'vaccination',
    title: 'First annual booster',
    due: addMonths(later(w(10), addDays(homeDate, 7)), 12),
    why: 'Leptospirosis protection needs topping up every year; the core diseases (distemper, hepatitis, parvo) usually every 3 years. The annual visit doubles as a full health check.',
    repeat: { every: 12, unit: 'month' },
  });
  add({
    template: 'kennel-cough',
    kind: 'vaccination',
    title: 'Ask about kennel cough vaccine',
    due: w(12),
    why: 'A squirt up the nose, usually required by kennels, daycare and many dog walkers and classes. Protection takes up to 3 weeks, so plan it well before any stay. Discuss at the 2nd vaccination.',
  });

  // --- Parasites ------------------------------------------------------------
  const firstWorm = later(homeDate, w(10));
  add({
    template: 'worming',
    kind: 'worming',
    title: 'Worming treatment',
    due: firstWorm,
    why: 'Puppies pick up roundworms from mum, so they’re wormed often while young. Lungworm (from eating slugs and snails — cockers hoover everything) is now a risk across much of the UK and only prescription products cover it, so most vets suggest a monthly one. Check with the breeder when he was last done.',
    repeat: { rule: 'puppyWorming' },
  });
  add({
    template: 'flea',
    kind: 'flea',
    title: 'Flea & tick treatment',
    due: firstWorm,
    why: 'Monthly for most products (many vet products combine flea, tick, worm and lungworm cover). Ticks peak April–June and September–November — spaniels love long grass and bracken, so check after walks.',
    repeat: { every: 1, unit: 'month' },
  });

  // --- Growth & grooming ----------------------------------------------------
  add({
    template: 'weigh',
    kind: 'weigh',
    title: 'Weigh-in',
    due: addDays(homeDate, 1),
    why: 'Regular weigh-ins keep food amounts in step with growth and flag problems early. Use kitchen or baby scales while he’s small, later weigh yourself holding him and subtract — or pop into the vet, most offer free weigh-ins.',
    repeat: { rule: 'puppyWeigh' },
  });
  add({
    template: 'ears',
    kind: 'grooming',
    title: 'Ear check',
    due: addDays(homeDate, 2),
    why: 'Cockers’ long, heavy ears trap warmth and moisture — ear infections affect about 1 in 10 UK cockers every year. Look and sniff weekly: redness, smell, dark wax or head shaking means a vet visit. Dry them after swims and baths.',
    repeat: { every: 1, unit: 'week' },
  });
  add({
    template: 'nails',
    kind: 'grooming',
    title: 'Nail trim (or just handle paws + treat)',
    due: addDays(homeDate, 7),
    why: 'Short, positive paw-handling sessions now mean stress-free nail trims for life. Take one tiny tip off, treat, done.',
    repeat: { every: 3, unit: 'week' },
  });
  add({
    template: 'groomer-intro',
    kind: 'grooming',
    title: 'Puppy groomer introduction',
    due: later(w(16), addDays(homeDate, 21)),
    why: 'A short “puppy intro” visit (bath, dryer noise, face and feet tidy) once vaccinated builds confidence. Once his feathering comes in (from ~6 months) cockers usually need a professional groom every 6–8 weeks. Ask groomers not to pluck ear hair — vet dermatologists advise against routine plucking.',
  });

  // --- Training & development ----------------------------------------------
  add({
    template: 'puppy-class',
    kind: 'training',
    title: 'Book puppy classes',
    due: addDays(homeDate, 7),
    why: 'Good classes fill up — book now to start once vaccinated. Look for reward-based trainers (e.g. APDT, IMDT, Kennel Club Good Citizen Puppy Foundation).',
  });
  add({
    template: 'six-month-check',
    kind: 'vet',
    title: '6-month health check & neutering chat',
    due: addMonths(dob, 6),
    why: 'Check weight, teeth (retained baby teeth are common) and discuss neutering. BVA/BSAVA guidance (March 2026) says castrating male dogs should be a case-by-case decision, not routine, and not before physical maturity — for cockers, ask for a behaviour-informed view.',
  });

  if (region === 'ni') {
    add({
      template: 'ni-licence',
      kind: 'admin',
      title: 'Get a Northern Ireland dog licence',
      due: addDays(addMonths(dob, 6), -14),
      why: 'In Northern Ireland dogs must be licensed by your council (£12.50 a year; £5 if neutered) and microchipped. Puppies are exempt until 6 months if mum was licensed — otherwise from the start. Apply via nidirect.',
      repeat: { every: 12, unit: 'month' },
    });
  }

  return items;
}

export type CareState = 'done' | 'overdue' | 'due' | 'soon' | 'upcoming' | 'unscheduled';

export function careState(item: CareItem, now: Millis = Date.now()): CareState {
  if (item.done) return 'done';
  if (!item.due) return 'unscheduled';
  const d = daysBetween(today(now), item.due);
  if (d < 0) return 'overdue';
  if (d === 0) return 'due';
  if (d <= 7) return 'soon';
  return 'upcoming';
}

/** Open items ordered by due date. */
export function openCare(items: CareItem[]): CareItem[] {
  return items
    .filter((i) => !i.done && !i.dismissed)
    .sort((a, b) => (a.due ?? '9999').localeCompare(b.due ?? '9999'));
}

/**
 * Marking a repeating item done creates the next occurrence. Returns the
 * updated item and, if it repeats, the next one.
 */
export function completeCare(item: CareItem, doneOn: ISODate, by: string | undefined, profile: Pick<Profile, 'dob'>, newId: () => string) {
  const done: CareItem = { ...item, done: doneOn, doneBy: by };
  const next: CareItem | undefined = item.repeat
    ? {
        id: newId(),
        kind: item.kind,
        title: item.title,
        why: item.why,
        repeat: item.repeat,
        product: item.product,
        notes: item.notes,
        due: nextDue(item.repeat, doneOn, profile.dob),
      }
    : undefined;
  return { done, next };
}
